/**
 * Drop Generator — frontend shapes. API rows are the shared contract
 * (shared/src/drop-generator); the draft mirrors a container detail 1:1 so
 * dirty checking is a JSON compare of apiToDraft(persisted) vs draft.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DraftCreative } from './components/editor/state/creative-draft';
import type { DraftSendDefaults } from './components/editor/state/satellites/send-defaults';
import type { DraftGateway } from './drop-generator-gateway.types';
import type { DraftAutoSend, DraftGatewayVerdict, DraftHitAgain, DraftScaling, DraftVerdict } from './drop-generator-rule.types';
import type {
  DayPlanContainerRow,
  DayPlanDropRow,
  DayPlanPreview,
  DropGenAllListsMode,
  DropGenAutoSendConfig,
  DropGenContainerDetail,
  DropGenContainerKind,
  DropGenContainerSummary,
  DropGenListOverride,
  DropGenListRanking,
  DropGenGroupLinkRow,
  DropGenHourOptimizerConfig,
  DropGenOwnSections,
  DropGenPlanDetail,
  DropGenPlanRow,
  DropGenPlanWarning,
  DropGenSlotRow,
  DropGenWeekday,
} from '@trafficpulse/shared';
import { readHourOptimizer } from '@trafficpulse/shared';

export type {
  DayPlanContainerRow,
  DayPlanDropRow,
  DayPlanPreview,
  DropGenPlanWarning,
  DropGenContainerDetail,
  DropGenContainerSummary,
  DropGenGroupLinkRow,
  DropGenHourOptimizerConfig,
  DropGenPlanDetail,
  DropGenPlanRow,
  DropGenSlotRow,
};

/** The editor's slot row — id null until first save creates the real row.
 *  routeId/optionsOverrideJson have no UI yet but MUST round-trip: the
 *  slots PUT is a full replace, so omitting them wipes persisted pins. */
export interface DraftSlot {
  id: string | null;
  amount: number | null;
  listsByGroup?: Record<string, number> | null;
  amountsByGroup: Record<string, number> | null; // a group's own amount on this drop
  enabled: boolean;
  routeId: number | null;
  /** Phase 40 — the lane the pin picks (null = the route's first lane). */
  gatewayRowId: string | null;
  optionsOverrideJson: Record<string, unknown> | null;
  /** Phase 21 — the groups that send at this time (2+ = together). */
  listGroupIds: string[];
  /** Phase 21 — an exact minute; null = on the container's clock. */
  pinnedAtMin: number | null;
  /** 14/10/2026 — this drop goes even outside the country's window. */
  sendOutsideWindow?: boolean;
  listsRankByGroup: Record<string, number> | null; // 19/10/2026 — {groupId: k}: the group's #k list of the day (all lists)
}

export type { DraftGateway, DraftPoolRow, DraftSenderDefaults } from './drop-generator-gateway.types';

export type { DraftCreative };

/** One flat verdict condition row (the editor speaks flat ALL/ANY). */
export type {
  DraftFlatRule,
  DraftGatewayVerdict,
  DraftHitAgain,
  DraftScaling,
  DraftVerdict,
  DraftVerdictRow,
} from './drop-generator-rule.types';

export interface DraftGroupRef {
  listGroupId: string;
  orderMode: string;
  /** MANUAL's list sequence (wiserIds); kept even while inherited. */
  manualOrderJson: number[] | null;
  /** Phase 18 — FORMULA's list ranking; kept while another mode is picked. */
  orderFormulaJson: DropGenListRanking;
  listsPerDay: number; // 19/10/2026 — 1 = one list (default), N = all lists up to N, 0 = all
  allListsMode: DropGenAllListsMode; // with all lists: EACH = every drop to each list, ALTERNATE = they take turns
}

export type { DraftSendDefaults };

export interface ContainerDraft {
  /** Phase 14 — CONTAINER (a row of the day) | MASTER (a campaign's shared settings). */
  kind: DropGenContainerKind;
  /** The master this container follows; null = standalone (legacy). */
  masterId: string | null;
  /** Sections this container keeps as its own while following. */
  ownSections: DropGenOwnSections;
  /** creative.dropDefaultsJson keys the Options tab does not edit — carried
   *  opaque so a save never wipes them (aiOptimization*, staticTagId, rotators…). */
  sendDefaultsRaw: Record<string, unknown>;
  name: string;
  color: string;
  campaignWiserId: number | null;
  staticTagId: string | null;
  enabled: boolean;
  startMode: string;
  gapMin: number;
  startAtMin: number | null;
  endAtMin: number | null;
  intervalMin: number;
  /** Phase 33 — the recipient-local weekdays it runs (never empty on save). */
  runDays: DropGenWeekday[];
  /** Phase 33 — Saturday and Sunday send this % of each amount (100 = unchanged). */
  weekendAmountPct: number;
  /** 14/10/2026 — the container's drops go outside the country's window too. */
  sendOutsideWindow?: boolean;
  defaultAmount: number;
  failMode: string;
  hourOptimizer: DropGenHourOptimizerConfig;
  slots: DraftSlot[];
  groups: DraftGroupRef[];
  gateway: DraftGateway;
  creative: DraftCreative;
  verdict: DraftVerdict;
  /** Phase 10 — the container's scalingEnabled + the scaling rule. */
  scalingEnabled: boolean;
  scaling: DraftScaling;
  /** Phase 17 — the gateway verdict (verdict section). */
  gatewayVerdict: DraftGatewayVerdict;
  /** Phase 38 — the hit-again rule and its action (verdict section). */
  hitAgain: DraftHitAgain;
  /** Phase 18b — one list's own amount / gateway / send options (container-only). */
  listOverrides: DropGenListOverride[];
  sendDefaults: DraftSendDefaults;
  /** Phase 20 — the auto-send layer this row holds (a master config's =
   *  its group's; a container's = its own, used while ownSections.autoSend),
   *  and the saved one it is compared with on Save. */
  autoSend: DraftAutoSend;
  autoSendBase: DropGenAutoSendConfig;
}

/** Off, last 14 days, 20-min spacing, 4 extras, "Not enough data" under 200 visits (Phase 22). */
export const DEFAULT_HOUR_OPTIMIZER: DropGenHourOptimizerConfig = readHourOptimizer(undefined);

export const CONTAINER_COLOR_PRESETS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#14b8a6', '#0ea5e9', '#16a34a', '#64748b'] as const;
